import * as React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { isRequestActive } from "redux-request-loading";
import { Modal, Button, Loader } from "semantic-ui-react";

import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";
import { goToParent } from "@teamprevent/common/routing";

import { ADMIN_ROLES } from "@teamprevent/common/auth/roles";

import { ClinicErrors, ClinicDetails, isMobileClinic } from "../models";
import { editClinicClinicians } from "../actions";
import { EDIT_CLINIC_CLINICIANS } from "../actionTypes";
import { ApplicationState } from "../../../applicationState";
import { ClinicTypeNames } from "../../models";
import { PartialCustomerListModel } from "../../../crud/customer";
import { deleteAppointment } from "../../actions";
import { DELETE_APPOINTMENT } from "../../actionTypes";
import { Authorize } from "../../../auth/Authorize";

import { StandardClinicEdit } from "./StandardClinicEdit";
import { MobileClinicEdit } from "./MobileClinicEdit";
import { CloneClinicModal } from "./CloneClinicModal";
import { EditClinicButton } from "./EditClinicButton";
import { BlockClinicButton } from "./BlockClinicButton";

interface StateProps {
    clinic: ClinicDetails | undefined;
    isModalOpen: boolean;
    clinicErrors: ClinicErrors | undefined;
    basePath: string;
    saving: boolean;
    deleting: boolean;
    customers: PartialCustomerListModel[];
}

interface DispatchProps {
    close: () => void;
    save: (basePath: string, clinicId: string, clinicianIds: string[]) => void;
    delete: (clinicId: string) => void;
}

interface MergedProps {
    clinic: ClinicDetails | undefined;
    isModalOpen: boolean;
    clinicErrors: ClinicErrors | undefined;
    saving: boolean;
    deleting: boolean;
    close: () => void;
    save: (clinicianIds: string[]) => void;
    delete: () => void;
    customers: PartialCustomerListModel[];
}

export const EditModalUnconnected: React.FunctionComponent<MergedProps> = (props: MergedProps) => {
    const [showConflictErrors, setShowConflictErrors] = React.useState<boolean>(false);
    const [teamPreventUsers, setTeamPreventUsers] = React.useState<{ id: string; name: string }[]>(props.clinic?.teamPreventUsers ?? []);
    const [edited, setEdited] = React.useState<boolean>(false);
    const [cloning, setCloning] = React.useState<boolean>(false);

    // delete is a reserved keyword, hence the need to rename it below
    const { close, save, delete: propsDelete } = props;

    React.useEffect(() => {
        const newClinicians = props.clinic?.teamPreventUsers ?? [];
        setTeamPreventUsers(newClinicians);
    }, [props.clinic]);

    const onClose = React.useCallback(() => {
        setEdited(false);
        setShowConflictErrors(false);
        close();
    }, [close]);

    const onSave = React.useCallback(() => {
        setShowConflictErrors(true);
        if (teamPreventUsers.length <= 1) {
            save(teamPreventUsers.map(c => c.id));
        }
    }, [save, teamPreventUsers]);

    const onCliniciansChanged = React.useCallback((clinicians: { id: string; name: string }[]) => {
        setShowConflictErrors(false);
        setTeamPreventUsers(clinicians);
        setEdited(true);
    }, []);

    const renderEditClinicForm = () => {
        const { clinic } = props;
        if (!clinic) {
            return <Loader />;
        } else if (isMobileClinic(clinic)) {
            return (
                <MobileClinicEdit
                    teamPreventUsers={teamPreventUsers}
                    clinic={clinic}
                    conflicts={showConflictErrors && props.clinicErrors && props.clinicErrors.errors.userClashIds}
                    onCliniciansChanged={onCliniciansChanged}
                />
            );
        } else {
            return (
                <StandardClinicEdit
                    teamPreventUsers={teamPreventUsers}
                    clinic={clinic}
                    conflicts={showConflictErrors && props.clinicErrors && props.clinicErrors.errors.userClashIds}
                    onCliniciansChanged={onCliniciansChanged}
                />
            );
        }
    };

    const setCloningFalse = React.useCallback(() => setCloning(false), []);
    const setCloningTrue = React.useCallback(() => setCloning(true), []);

    const { isModalOpen } = props;
    if (!props.clinic) {
        return null;
    }

    return (
        <Modal open={isModalOpen}>
            {cloning && <CloneClinicModal clinic={props.clinic} onClose={ setCloningFalse} />}
            <Modal.Header content={`${isMobileClinic(props.clinic) ? "Mobile " : ""}Clinic`} />
            <Modal.Content>
                {renderEditClinicForm()}
            </Modal.Content>
            <Modal.Actions>
                {(
                    props.clinic?.clinicType === ClinicTypeNames.Open
                    || (!isMobileClinic(props.clinic) && props.clinic?.clinicType === ClinicTypeNames.HealthSurveillance)
                ) && <BlockClinicButton clinic={props.clinic} />}
                <Authorize roles={[ADMIN_ROLES.SYSTEM_ADMIN, ADMIN_ROLES.SENIOR_ADMINISTRATOR, ADMIN_ROLES.ADMINISTRATOR]}>
                    {props.clinic && <EditClinicButton clinic={props.clinic} customers={props.customers} />}
                    <Button content="Clone" onClick={setCloningTrue} />
                    {(props.clinic && (!props.clinic.appointments || !props.clinic.appointments.length)) &&
                        <ConfirmedButton
                            icon="delete"
                            className="warning"
                            negative
                            content="Delete"
                            promptText="Are you sure you want to delete this clinic"
                            onClick={propsDelete}
                            disabled={props.deleting}
                            loading={props.deleting}
                        />}
                </Authorize>
                <Button content="Close" onClick={onClose} />
                {edited && <Button content="Save" onClick={onSave} disabled={props.saving} loading={props.saving} />}
            </Modal.Actions>
        </Modal>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => {
    const clinicId = state.router.params && state.router.params.clinicId;
    const clinic = clinicId && state.clinic.clinics.find(c => c.id === clinicId) || undefined;
    const appointmentId = state.router.params && state.router.params.appointmentId;
    return {
        clinic,
        isModalOpen: !!clinicId && appointmentId === undefined,
        clinicErrors: state.clinic.clinicErrors,
        basePath: state.router.pathname?.substring(0, state.router.pathname?.lastIndexOf("/")) ?? "",
        saving: isRequestActive(state, EDIT_CLINIC_CLINICIANS),
        deleting: isRequestActive(state, DELETE_APPOINTMENT),
        customers: state.customers
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, any>): DispatchProps => ({
    close: () => dispatch(goToParent()),
    save: (basePath, clinicId, clinicianIds) => dispatch(editClinicClinicians(basePath, clinicId, clinicianIds)),
    delete: async (clinicId) => {
        await dispatch(deleteAppointment(clinicId));
        dispatch(goToParent());
    }
});

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps): MergedProps => {
    const { basePath, ...otherStateProps } = stateProps;
    const close = () => dispatchProps.close();
    const save = (clinicianIds: string[]) => dispatchProps.save(basePath, stateProps.clinic?.id ?? "", clinicianIds);
    const dispatchDelete = () => dispatchProps.delete(stateProps.clinic?.id ?? "");
    return {
        ...otherStateProps,
        close,
        save,
        delete: dispatchDelete
    };
};

export const EditModal = connect(mapStateToProps, mapDispatchToProps, mergeProps)(EditModalUnconnected);
